import React, { useState } from 'react';
import { 
  Target, Search, ChevronDown, Building2, Wallet
} from 'lucide-react';
import { COLORS, CHART_COLORS } from '../../../constants/dashboard';
import ProgressRing from '../shared/ProgressRing';

const CampaignsView = ({
  campaigns,
  businessProfile,
  openEditCampaign,
  toggleCampaignStatus,
}) => {
  const [campaignFilter, setCampaignFilter] = useState('all');
  const [matchFilter, setMatchFilter] = useState('all');
  const [campaignSearch, setCampaignSearch] = useState('');

  return (
    <div className="flex flex-col h-full">
      {/* MY CAMPAIGNS */}
      <div className="flex-1 overflow-auto p-6 bg-gradient-to-br from-gray-50 to-blue-50/30">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Your Campaigns</h2>
            <p className="text-sm text-gray-500">{campaigns.filter(c => c.status === 'active').length} active • {campaigns.filter(c => c.status === 'completed').length} completed • {campaigns.length} total</p>
          </div>
          <button 
            onClick={() => window.dispatchEvent(new CustomEvent('navigate-to', { detail: 'discover' }))}
            className="px-5 py-2.5 text-white rounded-xl font-semibold transition-all hover:shadow-lg"
            style={{ background: 'linear-gradient(135deg, #2563EB 0%, #1E40AF 100%)' }}
          >
            + New Campaign
          </button>
        </div>

          {/* Search & Filters */}
          {campaigns.length > 0 && (
            <div className="flex items-center gap-3 mb-5">
              {/* Status Filter Tabs */}
              <div className="flex gap-1 p-1 bg-gray-100 rounded-xl" data-testid="campaign-status-filter">
                {[
                  { id: 'all', label: 'All', count: campaigns.length },
                  { id: 'active', label: 'Active', count: campaigns.filter(c => c.status === 'active').length },
                  { id: 'completed', label: 'Completed', count: campaigns.filter(c => c.status === 'completed').length },
                  { id: 'paused', label: 'Paused', count: campaigns.filter(c => c.status === 'paused').length },
                ].map(f => (
                  <button
                    key={f.id}
                    data-testid={`filter-${f.id}`}
                    onClick={() => setCampaignFilter(f.id)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      campaignFilter === f.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {f.label} {f.count > 0 && <span className="ml-1 text-xs text-gray-400">({f.count})</span>}
                  </button>
                ))}
              </div>

              {/* Match Filter */}
              <select
                data-testid="match-filter"
                value={matchFilter}
                onChange={e => setMatchFilter(e.target.value)}
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-600 bg-white focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Campaigns</option>
                <option value="matched">With Match</option>
                <option value="unmatched">No Match</option>
              </select>

              {/* Search */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  data-testid="campaign-search"
                  type="text"
                  value={campaignSearch}
                  onChange={e => setCampaignSearch(e.target.value)}
                  placeholder="Search campaigns or charities..."
                  className="w-full pl-9 pr-3 py-1.5 border border-gray-200 rounded-lg text-xs bg-white focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )}
          
          {campaigns.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border shadow-sm">
              <div className="w-24 h-24 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)' }}>
                <Target className="w-12 h-12 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No campaigns yet</h3>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">Create your first campaign to start making an impact and engaging your team in charitable giving.</p>
              <button 
                onClick={() => window.dispatchEvent(new CustomEvent('navigate-to', { detail: 'discover' }))}
                className="px-6 py-3 text-white rounded-xl font-semibold transition-all hover:shadow-lg"
                style={{ background: 'linear-gradient(135deg, #2563EB 0%, #1E40AF 100%)' }}
              >
                Discover Charities →
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              {campaigns
                .filter(c => campaignFilter === 'all' || c.status === campaignFilter)
                .filter(c => matchFilter === 'all' || (matchFilter === 'matched' ? c.employer_match_enabled : !c.employer_match_enabled))
                .filter(c => {
                  if (!campaignSearch.trim()) return true;
                  const q = campaignSearch.toLowerCase();
                  return (c.title || '').toLowerCase().includes(q) || (c.charity_name || '').toLowerCase().includes(q) || (c.description || '').toLowerCase().includes(q);
                })
                .map((campaign, idx) => {
                const progress = Math.min(((campaign.raised_amount || 0) / (campaign.goal_amount || 1)) * 100, 100);
                const isCompleted = campaign.status === 'completed';
                return (
                  <div key={campaign.campaign_id} className={`bg-white rounded-xl border shadow-sm hover:shadow-md transition-all overflow-hidden ${isCompleted ? 'opacity-85' : ''}`}>
                    <div className="h-1.5" style={{ background: isCompleted
                      ? 'linear-gradient(90deg, #2563EB, #38BDF8)'
                      : `linear-gradient(90deg, ${CHART_COLORS[idx % CHART_COLORS.length]}, ${CHART_COLORS[(idx + 1) % CHART_COLORS.length]})` }}></div>
                    <div className="p-4">
                      {/* Header */}
                      <div className="flex items-start gap-3 mb-2">
                        <div className="relative flex-shrink-0">
                          <ProgressRing progress={progress} size={48} strokeWidth={5} color={isCompleted ? '#38BDF8' : CHART_COLORS[idx % CHART_COLORS.length]} />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-[10px] font-bold text-gray-700">{progress.toFixed(0)}%</span>
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-sm text-gray-900 truncate leading-tight">{campaign.title}</h3>
                          <p className="text-xs text-gray-500 truncate">{campaign.charity_name}</p>
                        </div>
                        <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0 ${
                          isCompleted ? 'bg-emerald-100 text-emerald-700' :
                          campaign.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                        }`} data-testid={`campaign-status-${campaign.campaign_id}`}>
                          {isCompleted ? 'Done' : campaign.status === 'active' ? 'Active' : 'Paused'}
                        </span>
                      </div>

                      {/* Charity Info */}
                      {campaign.description && (
                        <p className="text-[11px] text-gray-500 line-clamp-2 mb-2 leading-relaxed">{campaign.description}</p>
                      )}

                      {/* Scores & Tags */}
                      <div className="flex flex-wrap gap-1 mb-2">
                        {campaign.esg_score > 0 && (
                          <span className="px-1.5 py-0.5 bg-green-50 text-green-700 text-[10px] font-medium rounded">ESG {campaign.esg_score}</span>
                        )}
                        {campaign.employer_match_enabled && (
                          <span className="px-1.5 py-0.5 bg-purple-50 text-purple-700 text-[10px] font-medium rounded flex items-center gap-0.5">
                            <Wallet className="w-2.5 h-2.5" /> {campaign.employer_match_ratio || 1}:1
                          </span>
                        )}
                        {campaign.impact_metric && (
                          <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-medium rounded">{campaign.impact_metric}</span>
                        )}
                      </div>

                      {/* Amount */}
                      <div className="flex items-center justify-between text-xs mb-3">
                        <span className="font-bold text-gray-900">${(campaign.raised_amount || 0).toLocaleString()}</span>
                        <span className="text-gray-400">of ${(campaign.goal_amount || 0).toLocaleString()}</span>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex gap-1.5 pt-2 border-t">
                        <button
                          onClick={() => openEditCampaign(campaign)}
                          className="flex-1 px-2 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-all"
                        >
                          Edit
                        </button>
                        {isCompleted ? (
                          <button
                            onClick={() => toggleCampaignStatus(campaign, 'active')}
                            className="flex-1 px-2 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-all"
                          >
                            Reactivate
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={() => toggleCampaignStatus(campaign)}
                              className={`flex-1 px-2 py-1.5 text-xs font-medium rounded-lg transition-all ${
                                campaign.status === 'active'
                                  ? 'text-orange-600 bg-orange-50 hover:bg-orange-100'
                                  : 'text-green-600 bg-green-50 hover:bg-green-100'
                              }`}
                            >
                              {campaign.status === 'active' ? 'Pause' : 'Activate'}
                            </button>
                            <button
                              onClick={() => toggleCampaignStatus(campaign, 'completed')}
                              className="flex-1 px-2 py-1.5 text-xs font-medium text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-all"
                              data-testid={`complete-campaign-${campaign.campaign_id}`}
                            >
                              Complete
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => window.open(`/impact/${businessProfile?.business_id}/campaign/${campaign.campaign_id}`, '_blank')}
                          className="px-2 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-lg transition-all"
                          title="Share"
                        >
                          Share
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
    </div>
  );
};

export default CampaignsView;
